import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import { hideSeoContent } from "web/redux/ducks/feed-seo-content";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { numberFormat } from "web/script/modules/formats";
import browser from "web/script/utils/browser";
import { RawBranchLink } from "web/script/utils/branch-io";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import history from "web/script/utils/history";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import classnames from "classnames";
import globals from "web/script/modules/globals";
import PaywallMarkup from "web/react/components/paywall-markup/paywall-markup";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import { setLoading } from "web/redux/ducks/feed-show-more";
import styles from "./feeds-show-more.css";

interface FeedShowMoreProps {
    initialVisibleProductCount: number;
    initialTotalProductCount: number;
    initialNextButtonUrl: string;
    initialCurrentPage: number;
    buttonText: string;
}

const PAYWALLED_CLASS_NAME = "paywalled-content";
const APP_MODAL_NAME = "send-to-app-on-feed-show-more-modal";
const IS_LOAD_PREVIOUS =
    environment.getFeature("uf_upper_funnel_journey") && environment.getFeature("uf_load_previous");

function feedShowMoreState(state: ReduxStoreState): any {
    return state.feedShowMoreReducer;
}

function ProgressBar({ currentNumber, maximumNumber }): JSX.Element {
    const progress = currentNumber / maximumNumber;
    return (
        <div className={styles.progressBar} role="presentation">
            <span
                className={styles.progressBarInner}
                style={{
                    width: progress * 100 + "%",
                }}
            />
        </div>
    );
}

export function FeedsShowMore({
    initialCurrentPage,
    initialVisibleProductCount,
    initialTotalProductCount,
    initialNextButtonUrl,
    buttonText,
}: FeedShowMoreProps): JSX.Element | null {
    const feedState = useReduxSelector(feedShowMoreState);
    const dispatch = useDispatch();
    let {
        currentPage,
        visibleProductCount,
        totalProductCount,
        showMoreNextUrl,
        isLoading,
    } = feedState;
    const blockModalIsOpen = !!useOverlayWatcher(APP_MODAL_NAME);

    let shouldBlockIos =
        browser.ios &&
        !userProfiler.isLoggedIn() &&
        environment.get("domainDefaultLanguageIsEnglish");
    const blockingTitle = gettext("feed.show_more.app_overlay_block.title_4");

    const [hasBlockedOnce, setHasBlockedOnce] = useState<boolean>(false);
    const [isBlocked, setIsBlocked] = useState<boolean>(false);
    const blockVariant = environment.getFeature("mg_feeds_show_more_android_block");

    const shouldBlockAndroid =
        browser.android &&
        !userProfiler.isLoggedIn() &&
        blockVariant !== "no_block" &&
        (blockVariant === "hard_block" || !hasBlockedOnce);

    useEffect(() => {
        setIsBlocked(shouldBlockIos || shouldBlockAndroid);
    }, [shouldBlockIos, shouldBlockAndroid]);

    // Initial values for server-side rendering
    if (currentPage === null) {
        currentPage = initialCurrentPage;
        visibleProductCount = initialVisibleProductCount;
        totalProductCount = initialTotalProductCount;
        showMoreNextUrl = initialNextButtonUrl;
    }

    let messageText = "";
    if (totalProductCount > 0 || visibleProductCount == 0) {
        // "Showing {current_product_count} of {total_product_count}"
        messageText = gettext("feed_pagination.summary_showing_products", {
            current_product_count: numberFormat(visibleProductCount),
            total_product_count: numberFormat(totalProductCount),
        });
    }

    function showMore(): void {
        const pageViewId = history.getCurrentState().pageViewId;
        history.pushState(new URL(showMoreNextUrl, globals.window.location.href), {
            saveScrollPosition: false,
            data: { pageViewId },
        });
        dispatch(hideSeoContent());
        dispatch(setLoading(true));
    }

    function onBlockModalClose(): void {
        history.popOverlay(APP_MODAL_NAME);
    }

    function onBlockModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay != APP_MODAL_NAME) {
            history.pushOverlay(APP_MODAL_NAME);
        }
    }

    function onClickShowMore(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        analytics.event("feeds_show_more", "click_show_more_button", "click");

        if (shouldBlockAndroid) {
            setHasBlockedOnce(true);
            dispatch(
                openCustomerCaptureOverlay({
                    captureType: "feeds_show_more",
                    next: showMoreNextUrl,
                })
            );
            return;
        } else if (shouldBlockIos) {
            onBlockModalOpen();
            return;
        }
        showMore();
    }

    let loadingHtml: JSX.Element | null = null;
    if (isLoading) {
        loadingHtml = <LoadingSpinner />;
    }

    return (
        <>
            <div
                className={classnames(styles.wrapper, {
                    [PAYWALLED_CLASS_NAME]: isBlocked,
                })}
            >
                <div className={styles.loadingWrapper}>
                    {IS_LOAD_PREVIOUS === false && loadingHtml}
                </div>

                <div className={styles.progressMessage}>
                    <p>{messageText}</p>
                </div>

                <ProgressBar
                    currentNumber={visibleProductCount}
                    maximumNumber={totalProductCount}
                />

                {showMoreNextUrl && (
                    <PrimaryButton
                        className={styles.showMoreButton}
                        href={showMoreNextUrl}
                        fullHeight={false}
                        fullWidth={false}
                        openInNewTab={false}
                        disabled={false}
                        onClick={onClickShowMore}
                    >
                        {IS_LOAD_PREVIOUS ? (
                            <>
                                <div className={styles.inlineButtonText}>{buttonText}</div>
                                <div
                                    className={classnames(
                                        styles.loadingWrapper,
                                        styles.inlineLoadingWrapper
                                    )}
                                >
                                    {loadingHtml}
                                </div>
                            </>
                        ) : (
                            <>{buttonText}</>
                        )}
                    </PrimaryButton>
                )}

                <SendToAppModal
                    title={blockingTitle}
                    url={RawBranchLink.ShowMore}
                    isOpen={blockModalIsOpen}
                    onClose={onBlockModalClose}
                    analyticsEventLabel={"feeds_show_more"}
                />
            </div>
            {/* the email capture overlay doesn't render the paywall markup so we should do it here for both blocks */}
            {isBlocked && <PaywallMarkup cssSelector={PAYWALLED_CLASS_NAME} />}
        </>
    );
}

ProgressBar.propTypes = {
    currentNumber: PropTypes.number,
    maximumNumber: PropTypes.number,
};

ProgressBar.defaultProps = {
    currentNumber: 0,
    maximumNumber: 1,
};

export default FeedsShowMore;
